import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  { v: "limpieza", l: "Limpieza" },
  { v: "tiempos", l: "Tiempos de espera" },
  { v: "trato", l: "Trato" },
  { v: "precios", l: "Precios" },
  { v: "seguridad", l: "Seguridad" },
  { v: "stock", l: "Stock / producto" },
  { v: "otros", l: "Otros" },
];

const FRANJAS = [
  { v: "aperitivo", l: "Aperitivo" },
  { v: "comida", l: "Comida" },
  { v: "tardeo", l: "Tardeo" },
  { v: "cena", l: "Cena" },
  { v: "noche", l: "Noche" },
];

export default function Sugerencias() {
  const [form, setForm] = useState({
    type: "sugerencia",
    category: "otros",
    message: "",
    franja: "tardeo",
    origen: "cliente",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Escribe tu mensaje antes de enviar.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/suggestions", {
        ...form,
        type: "sugerencia",
        origen: "cliente",
      });
      setSent(true);
      toast.success("¡Gracias! Mensaje enviado.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[60vh] grid place-items-center animate-in fade-in" data-testid="sugerencia-success">
        <div className="text-center max-w-md">
          <CheckCircle2 size={56} className="mx-auto text-primary" />
          <h1 className="font-display font-black text-3xl mt-4">Recibido</h1>
          <p className="text-muted-foreground mt-2">
            Tu mensaje ha llegado al equipo de Casa del Pueblo. Lo revisaremos cuanto antes.
          </p>
          <button
            className="mt-6 bg-primary text-primary-foreground px-5 py-3 rounded-full font-bold hover:brightness-110 transition"
            onClick={() => {
              setSent(false);
              setForm((f) => ({ ...f, message: "" }));
            }}
            data-testid="sugerencia-another-btn"
          >
            Enviar otro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="sugerencias-page">
      <header className="py-4">
        <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Tu opinión nos ayuda</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">Cuéntanos</h1>
        <p className="text-muted-foreground mt-2">
          Nos ayudas a cuidar el espacio. Todo mensaje llega directamente al equipo.
        </p>
      </header>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-border p-5 sm:p-6 space-y-4" data-testid="sugerencia-form">
        {/* Categoría */}
        <div>
          <label className="block text-sm font-bold mb-2">Categoría</label>
          <select
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            className="w-full rounded-xl border border-border bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="select-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.v} value={c.v}>{c.l}</option>
            ))}
          </select>
        </div>

        {/* Franja */}
        <div>
          <label className="block text-sm font-bold mb-2">¿Cuándo ocurrió?</label>
          <div className="flex flex-wrap gap-2">
            {FRANJAS.map((f) => (
              <button
                type="button"
                key={f.v}
                onClick={() => set("franja", f.v)}
                data-testid={`franja-${f.v}`}
                className={`px-3 py-2 rounded-full text-sm border transition ${
                  form.franja === f.v ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {/* Mensaje */}
        <div>
          <label className="block text-sm font-bold mb-2">Mensaje</label>
          <textarea
            rows={5}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            placeholder="Cuéntanos qué ha pasado o qué mejorarías..."
            className="w-full rounded-xl border border-border bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            data-testid="textarea-message"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3.5 rounded-full font-bold hover:brightness-110 disabled:opacity-60 transition"
          data-testid="sugerencia-submit-btn"
        >
          <Send size={16} />
          {loading ? "Enviando..." : "Enviar"}
        </button>
      </form>
    </div>
  );
}
